import { html, createSignal, createEffect } from 'onefold';
import {useKeyboard} from 'onefold/a11y';
import { PlayIcon, ExpandIcon, ShrinkIcon } from './SvgIcon';

/**
 * Strips TypeScript type annotations so code can execute as plain JS in the browser.
 */
function stripTypes(code: string): string {
  return code
    .replace(/^(export\s+)?(interface|type)\s+\w+[^]*?\n\}/gm, '')
    .replace(/\)\s*:\s*[A-Za-z<>\[\]|&\s,]+\s*\{/g, ') {')
    .replace(/\)\s*:\s*[A-Za-z<>\[\]|&\s,]+\s*=>/g, ') =>')
    .replace(/(const|let|var)\s+(\w+)\s*:\s*[A-Za-z<>\[\]|&\s,]+\s*=/g, '$1 $2 =')
    .replace(/(\w)\s*:\s*(?:[A-Z]\w*(?:<[^>]*>)?(?:\[\])?|string|number|boolean|void|any|unknown|never)(\s*[,)=])/g, '$1$2')
    .replace(/(\w)<[^>]+>\(/g, '$1(')
    .replace(/\s+as\s+[A-Z]\w*(?:<[^>]*>)?/g, '')
    .replace(/\n{3,}/g, '\n\n');
}

/**
 * Playground component inspired by the TypeScript Playground.
 * 
 * Features:
 * - Line numbers gutter
 * - Tabbed output: Result (rendered DOM) / Console (captured logs)
 * - Resizable panels via drag divider
 * - Auto-run with 800ms debounce on code changes
 * - Tab key inserts spaces
 * - Reset to initial code
 */
export function Playground(initialCode: string, title = 'Live Example', options?: {
  /** Allow localStorage/sessionStorage access (adds allow-same-origin). Default: false */
  allowStorage?: boolean;
  /** Allow network requests (fetch, WebSocket, SSE). Default: false */
  allowNetwork?: boolean;
  /** Output panel height in px. Default: 300 */
  height?: number;
  /** Auto-run code on load and on edit. Default: true */
  autoRun?: boolean;
  /** Hide the editor panel, show only the result. Default: false */
  hideEditor?: boolean;
}): Node {
  const opts = {
    allowStorage: false,
    allowNetwork: false,
    height: 300,
    autoRun: true,
    hideEditor: false,
    ...options,
  };

  // Build sandbox attribute based on options
  const sandboxParts = ['allow-scripts'];
  if (opts.allowStorage) sandboxParts.push('allow-same-origin');
  if (opts.allowNetwork) sandboxParts.push('allow-same-origin');
  const sandboxAttr = [...new Set(sandboxParts)].join(' ');
  const code = createSignal(initialCode.trim());
  const iframeRef = createSignal<HTMLIFrameElement | null>(null);
  const activeTab = createSignal<'result' | 'console'>('result');
  const logs = createSignal<string[]>([]);
  const lineCount = createSignal(initialCode.trim().split('\n').length);
  const running = createSignal(false);
  const isFullscreen = createSignal(false);

  const toggleFullscreen = () => {
    isFullscreen.set(v => !v);
    document.body.style.overflow = isFullscreen() ? 'hidden' : '';
  };

  // Keyboard shortcuts: Esc to exit fullscreen, Ctrl+Enter to run
  useKeyboard({
    'Escape': () => {
      if (isFullscreen()) {
        isFullscreen.set(false);
        document.body.style.overflow = '';
      }
    },
    'Ctrl+Enter': () => run(),
  });

  // --- Build the iframe srcdoc ---
  const buildSrcdoc = (source: string): string => {
    const jsCode = stripTypes(source);
    // Strip import statements — the full library is loaded via CDN
    const codeWithoutImports = jsCode.replace(/^\s*import\s+\{[^}]*\}\s+from\s+['"][^'"]*['"];?\s*$/gm, '');
    const escapedCode = JSON.stringify(codeWithoutImports).replace(/<\/script/gi, '<\\/script');

    return [
      '<!DOCTYPE html><html><head><meta charset="utf-8">',
      '<style>',
      '* { box-sizing: border-box; margin: 0; font-family: -apple-system, BlinkMacSystemFont, sans-serif; }',
      'body { padding: 12px; font-size: 14px; line-height: 1.6; color: #1a1a2e; }',
      'button { padding: 6px 12px; border-radius: 4px; border: 1px solid #e5e7eb; cursor: pointer; margin: 4px 4px 4px 0; background: #fff; }',
      'button:hover { background: #f3f4f6; }',
      'h1,h2,h3 { margin-bottom: 8px; }',
      'p { margin-bottom: 8px; }',
      'input,textarea,select { padding: 6px 10px; border: 1px solid #e5e7eb; border-radius: 4px; margin: 4px 0; font-size: 14px; }',
      'ul,ol { padding-left: 20px; }',
      'li { margin: 4px 0; }',
      '.error { color: #dc2626; font-family: monospace; font-size: 12px; white-space: pre-wrap; padding: 8px; background: #fef2f2; border-radius: 4px; }',
      '</style></head><body>',
      '<div id="app"></div>',
      '<script type="module">',
      '',
      '// localStorage/sessionStorage polyfill for sandboxed iframe',
      '(function() {',
      '  function createMemoryStorage() {',
      '    const store = new Map();',
      '    return {',
      '      getItem(k) { return store.has(k) ? store.get(k) : null; },',
      '      setItem(k, v) { store.set(k, String(v)); },',
      '      removeItem(k) { store.delete(k); },',
      '      clear() { store.clear(); },',
      '      get length() { return store.size; },',
      '      key(i) { return [...store.keys()][i] || null; },',
      '    };',
      '  }',
      '  try { localStorage.setItem("__test__","1"); localStorage.removeItem("__test__"); }',
      '  catch(e) {',
      '    Object.defineProperty(window, "localStorage", { value: createMemoryStorage(), writable: false });',
      '    Object.defineProperty(window, "sessionStorage", { value: createMemoryStorage(), writable: false });',
      '  }',
      '})();',
      '',
      '// Load the full onefold library from CDN',
      'import * as onefold from "https://cdn.jsdelivr.net/npm/onefold@latest/dist/onefold.full.min.js";',
      '',
      '// Expose all APIs as globals for playground code',
      'const { createSignal, createEffect, createComputed, batch, html, mount, css, cssValue,',
      '  Router, navigate, currentRoute, Link, configureRouter,',
      '  createStore, createResource, lazy, ErrorBoundary,',
      '  createToken, provide, inject, tryInject, runWithProviders,',
      '  VirtualList, Suspense, SuspenseAll, Transition, animateEnter, animateLeave,',
      '  createForm, required, email, minLength, maxLength, pattern, min, max, custom,',
      '  createHttpClient, createI18n, createPersisted, localStorageAdapter, sessionStorageAdapter,',
      '  createTheme, setPermissions, getPermissions, hasPermission, guard, guardedNode,',
      '  createObserver, createPluginHost, createWebSocket, createEventSource,',
      '  FocusTrap, announce, useKeyboard, SkipLink,',
      '  wrapImperative, embedForeign, setEffectHook, registerDirective,',
      '  loadRemote, configureSecurity, preloadRemote, clearRemoteCache,',
      '  component, getComponentRegistry, getComponentMeta, exportManifest, enableDevtools, disableDevtools,',
      '  renderHTML, raw,',
      '  formatDate, timeAgo, formatCurrency, formatNumber, truncate, slugify, pluralize, capitalize, debounce, throttle, pipe',
      '} = onefold;',
      '',
      '// Console capture',
      'const _logs = [];',
      'const _origLog = console.log;',
      'console.log = (...a) => { _logs.push(a.map(x => typeof x === "object" ? JSON.stringify(x,null,2) : String(x)).join(" ")); _origLog(...a); window.parent.postMessage({type:"pg-log",logs:[..._logs]},"*"); };',
      'console.warn = (...a) => console.log("[warn]", ...a);',
      'console.error = (...a) => console.log("[error]", ...a);',
      '',
      'const CODE = ' + escapedCode + ';',
      '',
      'try {',
      '  const fn = new Function(',
      '    "createSignal","createEffect","createComputed","batch","html","mount","css","cssValue",',
      '    "Router","navigate","currentRoute","Link","configureRouter",',
      '    "createStore","createResource","lazy","ErrorBoundary",',
      '    "createToken","provide","inject","tryInject","runWithProviders",',
      '    "VirtualList","Suspense","SuspenseAll","Transition","animateEnter","animateLeave",',
      '    "createForm","required","email","minLength","maxLength","pattern","min","max","custom",',
      '    "createHttpClient","createI18n","createPersisted","localStorageAdapter","sessionStorageAdapter",',
      '    "createTheme","setPermissions","getPermissions","hasPermission","guard","guardedNode",',
      '    "createObserver","createPluginHost","createWebSocket","createEventSource",',
      '    "FocusTrap","announce","useKeyboard","SkipLink",',
      '    "wrapImperative","embedForeign","setEffectHook","registerDirective",',
      '    "loadRemote","configureSecurity","preloadRemote","clearRemoteCache",',
      '    "component","getComponentRegistry","getComponentMeta","exportManifest","enableDevtools","disableDevtools",',
      '    "renderHTML","raw",',
      '    "formatDate","timeAgo","formatCurrency","formatNumber","truncate","slugify","pluralize","capitalize","debounce","throttle","pipe",',
      '    CODE + "\\n\\n" +',
      '    "if (typeof App===\\"function\\") mount(App(), document.getElementById(\\"app\\"));\\n" +',
      '    "else if (typeof Counter===\\"function\\") mount(Counter(), document.getElementById(\\"app\\"));\\n" +',
      '    "else if (typeof Main===\\"function\\") mount(Main(), document.getElementById(\\"app\\"));\\n" +',
      '    "else if (typeof Todo===\\"function\\") mount(Todo(), document.getElementById(\\"app\\"));\\n"',
      '  );',
      '  fn(',
      '    createSignal, createEffect, createComputed, batch, html, mount, css, cssValue,',
      '    Router, navigate, currentRoute, Link, configureRouter,',
      '    createStore, createResource, lazy, ErrorBoundary,',
      '    createToken, provide, inject, tryInject, runWithProviders,',
      '    VirtualList, Suspense, SuspenseAll, Transition, animateEnter, animateLeave,',
      '    createForm, required, email, minLength, maxLength, pattern, min, max, custom,',
      '    createHttpClient, createI18n, createPersisted, localStorageAdapter, sessionStorageAdapter,',
      '    createTheme, setPermissions, getPermissions, hasPermission, guard, guardedNode,',
      '    createObserver, createPluginHost, createWebSocket, createEventSource,',
      '    FocusTrap, announce, useKeyboard, SkipLink,',
      '    wrapImperative, embedForeign, setEffectHook, registerDirective,',
      '    loadRemote, configureSecurity, preloadRemote, clearRemoteCache,',
      '    component, getComponentRegistry, getComponentMeta, exportManifest, enableDevtools, disableDevtools,',
      '    renderHTML, raw,',
      '    formatDate, timeAgo, formatCurrency, formatNumber, truncate, slugify, pluralize, capitalize, debounce, throttle, pipe',
      '  );',
      '  window.parent.postMessage({type:"pg-ready"},"*");',
      '} catch(e) {',
      '  document.getElementById("app").innerHTML = \'<div class="error">\' + e.message + \'</div>\';',
      '  window.parent.postMessage({type:"pg-log",logs:["[error] " + e.message]},"*");',
      '}',
      '<\/script></body></html>',
    ].join('\n');
  };

  // --- Run ---
  const run = () => {
    const iframe = iframeRef();
    if (!iframe) return;
    running.set(true);
    logs.set([]);
    iframe.srcdoc = buildSrcdoc(code());
    setTimeout(() => running.set(false), 300);
  };

  // --- Debounced auto-run ---
  let autoRunTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleAutoRun = () => {
    if (!opts.autoRun) return;
    if (autoRunTimer) clearTimeout(autoRunTimer);
    autoRunTimer = setTimeout(run, 800);
  };

  // --- Event handlers ---
  const handleInput = (e: Event) => {
    const val = (e.target as HTMLTextAreaElement).value;
    code.set(val);
    lineCount.set(val.split('\n').length);
    scheduleAutoRun();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target as HTMLTextAreaElement;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      ta.value = ta.value.substring(0, start) + '  ' + ta.value.substring(end);
      ta.selectionStart = ta.selectionEnd = start + 2;
      code.set(ta.value);
      lineCount.set(ta.value.split('\n').length);
      scheduleAutoRun();
    }
  };

  const handleEditorScroll = (e: Event) => {
    const ta = e.target as HTMLTextAreaElement;
    const gutter = ta.previousElementSibling as HTMLElement;
    if (gutter) gutter.scrollTop = ta.scrollTop;
  };

  const resetCode = () => {
    code.set(initialCode.trim());
    lineCount.set(initialCode.trim().split('\n').length);
    // Update textarea value directly
    const ta = document.querySelector('.playground-editor') as HTMLTextAreaElement | null;
    if (ta) ta.value = initialCode.trim();
    run();
  };

  // --- Listen for postMessage from iframe ---
  if (typeof window !== 'undefined') {
    window.addEventListener('message', (e) => {
      if (e.data?.type === 'pg-log') {
        logs.set(e.data.logs ?? []);
      }
    });
  }

  // --- Resize divider ---
  const initDragResize = (divider: HTMLElement) => {
    let dragging = false;
    let startX = 0;
    let startWidth = 0;

    divider.addEventListener('mousedown', (e: MouseEvent) => {
      dragging = true;
      startX = e.clientX;
      startWidth = (divider.previousElementSibling as HTMLElement).getBoundingClientRect().width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!dragging) return;
      const container = divider.parentElement as HTMLElement;
      const maxW = container.getBoundingClientRect().width - 120;
      const newW = Math.max(120, Math.min(maxW, startWidth + (e.clientX - startX)));
      const pct = (newW / container.getBoundingClientRect().width) * 100;
      (divider.previousElementSibling as HTMLElement).style.cssText = `flex:none;width:${pct}%;min-width:120px`;
      (divider.nextElementSibling as HTMLElement).style.flex = '1';
    });

    document.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    });
  };

  // --- Auto-run on first mount ---
  if (opts.autoRun) setTimeout(run, 200);

  // --- Render ---
  return html`
    <div class=${() => 'playground' + (isFullscreen() ? ' playground-fullscreen' : '')}>
      <div class="playground-toolbar">
        <div class="playground-toolbar-left">
          <span class="playground-title">${title}</span>
        </div>
        <div class="playground-toolbar-right">
          <button class="pg-btn pg-btn-run" onclick=${run}>
            ${PlayIcon()}
            Run
          </button>
          ${opts.hideEditor ? null : html`<button class="pg-btn" onclick=${resetCode}>Reset</button>`}
          <button class="pg-btn" onclick=${toggleFullscreen} title="Toggle fullscreen (Esc to exit)">
            ${() => isFullscreen() ? ShrinkIcon() : ExpandIcon()}
          </button>
        </div>
      </div>
      <div class="playground-body" style=${{ height: opts.hideEditor ? `${opts.height}px` : undefined }}>
        ${opts.hideEditor ? null : html`
          <div class="playground-left">
            <div class="pg-gutter">${() =>
              Array.from({ length: lineCount() }, (_, i) =>
                html`<div class="pg-line-num">${String(i + 1)}</div>`
              )
            }</div>
            <textarea
              class="playground-editor"
              oninput=${handleInput}
              onkeydown=${handleKeyDown}
              onscroll=${handleEditorScroll}
              spellcheck="false"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
            >${initialCode.trim()}</textarea>
          </div>
          <div class="playground-divider" ref=${(el: HTMLElement) => initDragResize(el)}></div>
        `}
        <div class=${opts.hideEditor ? 'playground-right playground-full-width' : 'playground-right'}>
          <div class="pg-tabs">
            <button class=${() => 'pg-tab' + (activeTab() === 'result' ? ' active' : '')} onclick=${() => activeTab.set('result')}>Result</button>
            <button class=${() => 'pg-tab' + (activeTab() === 'console' ? ' active' : '')} onclick=${() => activeTab.set('console')}>
              Console${() => logs().length > 0 ? html`<span class="pg-tab-badge">${String(logs().length)}</span>` : ''}
            </button>
          </div>
          <div class="pg-output-result" style=${() => ({ display: activeTab() === 'result' ? '' : 'none', height: `${opts.height}px` })}>
            <iframe
              ref=${(el: HTMLIFrameElement) => iframeRef.set(el)}
              sandbox=${sandboxAttr}
            ></iframe>
          </div>
          <div class="pg-output-console" style=${() => activeTab() === 'console' ? '' : 'display:none'}>
            ${() => logs().length === 0
              ? html`<div class="pg-console-empty">No output. Run the code to see console.log results.</div>`
              : html`<div class="pg-console-entries">${logs().map(l => html`<div class="pg-console-line"><span class="pg-console-chevron">${'>'}</span> ${l}</div>`)}</div>`
            }
          </div>
        </div>
      </div>
    </div>
  `;
}
